/**
 * mavin-recommender.js  -  MavinWear Size Chart + Size Recommendation
 * --------------------------------------------------------------------
 * One branded popup with two tabs:
 *   1) Size Chart            - the product's garment measurements (cm)
 *   2) Size Recommendation   - enter height + weight, get the size instantly
 *
 * Two links ("Size chart" / "Size recommendation") are injected next to the
 * product's size selector. The recommender math is calibrated against
 * MavinWear's own height/weight matrix (100% match on the reference tee).
 *
 * Data + algorithm are client-side (no backend). ASCII-only (Shopify rule).
 */
(function () {
  'use strict';

  // ---- Garment data (flat half-chest width + total length, cm) ------------
  var CHARTS = {
    'compression':   { name: 'Compression Tee',  fit: 'compression', type: 'top',
                       sizes: ['S','M','L','XL'], length: [60,63,67,67.5], chest: [39,42.5,45,49.5] },
    'slim-tee':      { name: 'Rival Slim Tee',    fit: 'slim',        type: 'top',
                       sizes: ['S','M','L','XL'], length: [70,71,73,76], chest: [50.5,52,53,58] },
    'oversized-tee': { name: 'Rival Oversized Tee', fit: 'oversized', type: 'top',
                       sizes: ['S','M','L','XL'], length: [69,71,73,76], chest: [59,61,63,68] },
    'oversized-tank':{ name: 'Rival Oversized Tank', fit: 'oversized', type: 'top',
                       sizes: ['S','M','L','XL'], length: [69,71,73,77], chest: [59,60,63,67] },
    'tank-rib':      { name: 'Rib / Contrast Tank', fit: 'compression', type: 'top',
                       sizes: ['S','M','L','XL'], length: [58.5,60.5,62.5,64.5], chest: [36,39,42,47] },
    'contour':       { name: 'Contour Fit Tee',   fit: 'slim',        type: 'top',
                       sizes: ['S','M','L','XL'], length: [66,68,70,72], chest: [45,49,53,57] },
    'sweatpants':    { name: 'Sweatpants',        fit: 'regular',     type: 'bottom',
                       sizes: ['S','M','L','XL'], inseam: [74,76,78,82], totalLength: [105,107,109,112],
                       waist: [36,37,39,40], hips: [53,56,60,68] }
  };

  // ---- Per-product-type fallback charts -----------------------------------
  // Used ONLY for the recommendation when a product has no assigned size chart
  // (e.g. jerseys, shorts). Tuned so an average build (about 175 cm / 75 kg)
  // lands on M for that garment type. These never show a chart - recommend only.
  // Each averaged from ALL of that type's real size-chart pages (see
  // scripts/retune.py). Used only for the recommendation when a product has no
  // chart of its own; never displayed as a chart.
  var FALLBACK = {
    'tee':         { name: 'T-Shirt',   fit: 'oversized',   type: 'top',
                     sizes: ['S','M','L','XL'], length: [68.5,70.5,72.5,75], chest: [56.5,59.5,62.5,67] },
    'jersey':      { name: 'Jersey',    fit: 'oversized',   type: 'top',
                     sizes: ['S','M','L','XL'], length: [69.7,71.7,73.7,75.7], chest: [58.3,61.7,65,68.3] },
    'tank':        { name: 'Tank Top',  fit: 'compression', type: 'top',
                     sizes: ['S','M','L','XL'], length: [60,62,64,66], chest: [38,41,44,48] },
    'compression': { name: 'Compression', fit: 'compression', type: 'top',
                     sizes: ['S','M','L','XL'], length: [63.3,65.7,68.3,69.8], chest: [38.3,42.2,45.7,49.8] },
    'hoodie':      { name: 'Hoodie',    fit: 'regular',     type: 'top',
                     sizes: ['S','M','L','XL'], length: [66,68,70,72], chest: [56,60,64,68] },
    'jacket':      { name: 'Jacket',    fit: 'oversized',   type: 'top',
                     sizes: ['S','M','L','XL'], length: [66,68,70,72], chest: [60,62,64,66] },
    'shorts':      { name: 'Shorts',    fit: 'regular',     type: 'bottom',
                     sizes: ['S','M','L','XL'], inseam: [22,23,24,25], waist: [33,36,39,42], hips: [53,55,57,59] },
    'sweatpants':  { name: 'Sweatpants', fit: 'regular',    type: 'bottom',
                     sizes: ['S','M','L','XL'], inseam: [74,76,78,82], waist: [36,37,39,40], hips: [53,56,60,68] }
  };

  // Per-type ease (cm), re-tuned from 3,000+ real exchanges (scripts/retune.py).
  // + ease = more room (style runs small -> recommend bigger for borderline
  // builds); - ease = less room (runs large). Clamped so the typical build's
  // size never flips - only borderline shoppers move, in the data's direction.
  var EASE_BY_TYPE = {
    'compression': -5.2, 'slim-tee': 14.1, 'contour': 4.4,
    'oversized-tee': 14.1, 'oversized-tank': 13, 'tank-rib': -13.6,
    'tee': 14.1, 'jersey': 16.4, 'shorts': -0.9, 'sweatpants': -4
  };

  // ---- Base size = body model, calibrated to the owner's General Size Chart ----
  // The ABSOLUTE size comes from height + weight only, garment-independent, so it
  // transfers to every style (fitted OR oversized) without collapsing. It reproduces
  // MavinWear's own height/weight matrix (see projects/mavin-size-recommender/
  // calibrate.py) at 94% agreement.
  //   score = W + KH*(H-170);  bands: <62 S | <77 M | <89 L | >=89 XL
  // Weight is the dominant driver (as it should be); height shifts by ~0.75 kg-equiv
  // per cm. Verified: 180/50 -> S, 180/65 -> M, 180/73 -> L, 180/80 -> L, 180/90 -> XL.
  // (The old garment-chest comparison silently ignored weight on baggy/oversized
  //  garments -- their smallest chart size already exceeds any realistic body chest,
  //  so every weight collapsed to S and the HEIGHT band alone decided the size. That
  //  made 180/50 and 180/80 both resolve to the same size.)
  var MATRIX_KH = 0.75;
  var MATRIX_THRESH = [62, 77, 89];
  function bodyScore(H, W) { return W + MATRIX_KH * (H - 170); }
  function scoreIndex(s) { return s < MATRIX_THRESH[0] ? 0 : s < MATRIX_THRESH[1] ? 1 : s < MATRIX_THRESH[2] ? 2 : 3; }

  // ---- Fit direction + strength (drives the fractional nudge in recommend()) --------------
  // The exchange data proves the DIRECTION a style runs (returns carry no height/weight) AND -- via
  // the Wilson lower bound -- HOW STRONGLY it skews. We turn that into a FRACTIONAL size nudge, not a
  // blunt whole-size flip: a weak skew barely moves anyone, a strong skew moves the upper part of a
  // size band, and every size (including S) stays reachable.
  var GATE = 0.65;                 // fit_insight confidence gate (matches the mavin-fit worker)
  var SIGMA_FULL = 0.88;           // LB at/above which the skew is treated as full strength (sigma=1)
  var BASE_FIT = 'normal';         // manual per-product override (mavin.size_fit -> data-fit)
  var FIT_PRIOR = { compression: 1, slim: 1, oversized: -1, regular: 0 }; // cold-start prior (no verdict yet)
  var CURRENT_FIT_CLASS = 'regular';
  // Shopper fit preference (Snug / True to size / Relaxed) -> fractional shift. Captures build +
  // preference that height/weight alone cannot. Default 'true' reproduces the calibrated body model.
  var PREF_SHIFT = { snug: -0.35, 'true': 0, relaxed: 0.35 };
  var CURRENT_PREF = 'true';

  // Resolve the live exchange skew into { dir:-1|0|1, sigma:0..1 }. Priority: live Estebdal verdict
  // (window.MavinFit.dir + .lb) -> manual override -> cold-start fit-class prior.
  function resolveSkew() {
    var mf = window.MavinFit || {};
    var d = mf.dir || null, lb = (typeof mf.lb === 'number') ? mf.lb : 0;
    var dir = 0, sigma = 0;
    if (d === 'up' || d === 'down') {
      dir = d === 'up' ? 1 : -1;
      sigma = lb > 0 ? Math.max(0, Math.min(1, (lb - GATE) / (SIGMA_FULL - GATE))) : 0.5;
    } else if (d === 'true') {
      dir = 0; sigma = 0;
    } else if (BASE_FIT === 'small') {
      dir = 1; sigma = 0.5;                                       // manual override
    } else if (BASE_FIT === 'large') {
      dir = -1; sigma = 0.5;
    } else {
      dir = FIT_PRIOR[CURRENT_FIT_CLASS] || 0; sigma = dir ? 0.35 : 0;   // gentle prior until real data
    }
    return { dir: dir, sigma: sigma };
  }

  // Garment-measurement cross-check: does THIS chart's chest run tighter/looser than its same-fit-class
  // peers among the built-in charts? Returns 1 (tighter -> runs small), -1 (roomier -> runs large),
  // 0 (in line), or null (not enough peers / no chest data). Used ONLY to DAMPEN the exchange nudge
  // when measurements disagree with the exchange verdict -- measurements inform, never override the base.
  function garmentRunDir(chart) {
    if (!chart || chart.type !== 'top' || !chart.chest || !chart.chest.length) return null;
    var fit = chart.fit || CURRENT_FIT_CLASS, sum = [], cnt = 0;
    for (var k in CHARTS) {
      var c = CHARTS[k];
      if (c.type !== 'top' || c.fit !== fit || !c.chest) continue;
      for (var i = 0; i < c.chest.length; i++) sum[i] = (sum[i] || 0) + c.chest[i];
      cnt++;
    }
    if (cnt < 2) return null;                                     // need peers besides itself
    var mine = 0, ref = 0, m = Math.min(chart.chest.length, sum.length), used = 0;
    for (var j = 0; j < m; j++) { mine += chart.chest[j]; ref += sum[j] / cnt; used++; }
    if (!used) return null;
    var delta = (mine - ref) / used;                              // + roomier than peers, - tighter
    return delta <= -1.2 ? 1 : (delta >= 1.2 ? -1 : 0);
  }

  // Ease (cm) added to body chest, per garment fit class. Calibrated so the
  // slim tee reproduces the owner's height/weight matrix exactly.
  var EASE = { compression: -8, slim: 12, regular: 12, oversized: 18 };

  // ---- Inline icons (currentColor) ----------------------------------------
  var RULER_SVG = '<svg class="mr-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="10" rx="1"></rect><line x1="7" y1="7" x2="7" y2="11"></line><line x1="12" y1="7" x2="12" y2="12"></line><line x1="17" y1="7" x2="17" y2="11"></line></svg>';
  var CHECK_SVG = '<svg class="mr-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"></path></svg>';

  // 2D garment diagram (illustrates the measured Length + Width to the shopper).
  function diagram(chart) {
    var O = '#e8732e';   // measurement-arrow accent (matches the reference diagram)
    if (chart.type === 'bottom') {
      return '<div class="mr-figure"><svg viewBox="0 0 200 200" width="150" height="150" role="img" aria-label="How we measure">' +
        '<path d="M58 18 H142 V58 L150 184 H120 L102 86 L98 86 L80 184 H50 L58 58 Z" fill="#fafafa" stroke="#111" stroke-width="3" stroke-linejoin="round"></path>' +
        '<line x1="60" y1="30" x2="140" y2="30" stroke="' + O + '" stroke-width="2"></line>' +
        '<polygon points="56,30 66,26 66,34" fill="' + O + '"></polygon><polygon points="144,30 134,26 134,34" fill="' + O + '"></polygon>' +
        '<text x="100" y="24" font-size="12" fill="' + O + '" font-weight="700" text-anchor="middle">Waist</text>' +
        '<line x1="92" y1="90" x2="92" y2="182" stroke="' + O + '" stroke-width="2"></line>' +
        '<polygon points="92,86 88,96 96,96" fill="' + O + '"></polygon><polygon points="92,186 88,176 96,176" fill="' + O + '"></polygon>' +
        '<text x="100" y="140" font-size="12" fill="' + O + '" font-weight="700">Inseam</text>' +
        '</svg></div>';
    }
    return '<div class="mr-figure"><svg viewBox="0 0 240 184" width="184" height="141" role="img" aria-label="How we measure">' +
      '<path d="M84 16 L50 30 L26 58 L50 80 L70 60 L70 164 L170 164 L170 60 L190 80 L214 58 L190 30 L156 16 C156 35 132 45 120 45 C108 45 84 35 84 16 Z" fill="#fafafa" stroke="#111" stroke-width="3" stroke-linejoin="round"></path>' +
      '<line x1="86" y1="56" x2="86" y2="162" stroke="' + O + '" stroke-width="2"></line>' +
      '<polygon points="86,52 82,62 90,62" fill="' + O + '"></polygon><polygon points="86,166 82,156 90,156" fill="' + O + '"></polygon>' +
      '<text x="94" y="115" font-size="12" fill="' + O + '" font-weight="700">Length</text>' +
      '<line x1="74" y1="76" x2="166" y2="76" stroke="' + O + '" stroke-width="2"></line>' +
      '<polygon points="70,76 80,72 80,80" fill="' + O + '"></polygon><polygon points="170,76 160,72 160,80" fill="' + O + '"></polygon>' +
      '<text x="120" y="70" font-size="12" fill="' + O + '" font-weight="700" text-anchor="middle">Chest width</text>' +
      '</svg></div>';
  }

  // ---- Core math ----------------------------------------------------------
  // Returns { rec, neighbor, neighborNote, confidence, adjusted, dir, pref }.
  // Continuous size coordinate = calibrated body model + FRACTIONAL nudges (preference + exchange
  // skew), rounded once. Neutral preference + no skew reproduces the body model exactly.
  function recommend(chart, H, W) {
    var last = chart.sizes.length - 1;           // 3 for S/M/L/XL
    var s = bodyScore(H, W);
    var base = Math.min(scoreIndex(s), last);

    // Position within the base size's score band (0 = bottom/tighter, 1 = top/looser).
    var lo = base > 0 ? MATRIX_THRESH[base - 1] : MATRIX_THRESH[0] - 14;
    var hi = base < 3 ? MATRIX_THRESH[base] : MATRIX_THRESH[2] + 14;
    var band = hi - lo;
    var pos = band > 0 ? Math.max(0, Math.min(1, (s - lo) / band)) : 0.5;

    // Continuous size coordinate: integer = band centre, +-0.5 = a band edge. At neutral preference
    // with no skew this rounds back to `base`, so the calibrated body model is preserved exactly.
    var fpos = base + (pos - 0.5);
    var pref = PREF_SHIFT[CURRENT_PREF] || 0;
    var skew = resolveSkew();

    // Soften the exchange nudge when the garment's own measurements disagree with the exchange verdict.
    var gc = garmentRunDir(chart), sigma = skew.sigma;
    if (skew.dir !== 0 && gc !== null && gc !== 0 && gc !== skew.dir) sigma *= 0.5;
    var exShift = skew.dir * sigma * 0.5;        // exchange nudge, at most +-0.5 of a size

    // Compression is tight by design: the exchange up-skew never pushes the PRIMARY up (offered as a
    // looser neighbor instead). The shopper's own "relaxed" choice is still honoured.
    var exPrimary = (CURRENT_FIT_CLASS === 'compression' && exShift > 0) ? 0 : exShift;

    var target = fpos + pref + exPrimary;
    var rec = Math.max(0, Math.min(last, Math.round(target)));
    var frac = target - rec;                      // -0.5..0.5 offset from the chosen size

    // At most one neighbour, on whichever side the shopper leans.
    var neighbor = -1, note = '';
    if (frac > 0.18 && rec < last) { neighbor = rec + 1; note = 'if you prefer a looser fit'; }
    else if (frac < -0.18 && rec > 0) { neighbor = rec - 1; note = 'if you prefer a tighter, fitted look'; }

    // Compression: surface the looser size (the suppressed up-skew) for upper-band builds only,
    // so a shopper who is clearly the smallest size isn't nudged toward an unnecessary upsell.
    if (CURRENT_FIT_CLASS === 'compression' && neighbor === -1 && rec < last && pos >= 0.5) {
      neighbor = rec + 1; note = 'if you prefer a looser, less-compressive fit';
    }

    var confidence = (Math.abs(frac) <= 0.25) ? 'high' : 'medium';
    return { rec: rec, neighbor: neighbor, neighborNote: note, confidence: confidence,
             adjusted: rec !== base, dir: skew.dir, pref: CURRENT_PREF };
  }

  // ---- Build the modal once -----------------------------------------------
  // showChart / showRec gate which tab(s) appear (theme toggles + fallback mode).
  function buildModal(chart, activeTab, showChart, showRec) {
    var both = showChart && showRec;

    var chartPanel = '';
    if (showChart) {
      var sizeRows = '';
      for (var i = 0; i < chart.sizes.length; i++) {
        var c2, c3;
        if (chart.type === 'top') { c2 = chart.chest[i]; c3 = chart.length[i]; }
        else { c2 = chart.waist[i]; c3 = chart.inseam[i]; }
        sizeRows += '<tr><td class="mr-sz">' + chart.sizes[i] + '</td><td>' + c2 + '</td><td>' + c3 + '</td></tr>';
      }
      var head2 = chart.type === 'top' ? 'Chest (cm)' : 'Waist (cm)';
      var head3 = chart.type === 'top' ? 'Length (cm)' : 'Inseam (cm)';
      chartPanel =
        '<div class="mr-panel" data-panel="chart">' +
          '<p class="mr-sub">' + chart.name + ' - measurements in cm</p>' +
          diagram(chart) +
          '<table class="mr-table"><thead><tr><th>Size</th><th>' + head2 + '</th><th>' + head3 + '</th></tr></thead>' +
          '<tbody>' + sizeRows + '</tbody></table>' +
          '<p class="mr-foot">Measured flat. Chest = pit-to-pit. If between sizes, size up for a looser fit.</p>' +
        '</div>';
    }

    var recPanel = '';
    if (showRec) {
      recPanel =
        '<div class="mr-panel" data-panel="rec">' +
          '<p class="mr-sub">FIND YOUR SIZE</p>' +
          '<div class="mr-fields">' +
            '<label class="mr-field"><span>Height</span><div class="mr-input"><input type="number" inputmode="numeric" id="mr-h" min="120" max="220" placeholder="175"><em>cm</em></div></label>' +
            '<label class="mr-field"><span>Weight</span><div class="mr-input"><input type="number" inputmode="numeric" id="mr-w" min="35" max="180" placeholder="75"><em>kg</em></div></label>' +
          '</div>' +
          '<div class="mr-pref" role="group" aria-label="Fit preference">' +
            '<span class="mr-pref-lbl">How do you like it to fit?</span>' +
            '<div class="mr-pref-row">' +
              '<button type="button" class="mr-pref-btn" data-pref="snug">Snug</button>' +
              '<button type="button" class="mr-pref-btn mr-pref-btn--on" data-pref="true">True to size</button>' +
              '<button type="button" class="mr-pref-btn" data-pref="relaxed">Relaxed</button>' +
            '</div>' +
          '</div>' +
          '<button class="mr-go" type="button">Find my size</button>' +
          '<div class="mr-result" hidden></div>' +
        '</div>';
    }

    var tabs = both
      ? '<div class="mr-tabs">' +
          '<button class="mr-tab" data-tab="chart">SIZE CHART</button>' +
          '<button class="mr-tab" data-tab="rec">SIZE RECOMMENDATION</button>' +
        '</div>'
      : '';

    var html =
      '<div class="mr-overlay" role="dialog" aria-modal="true" aria-label="Size guide">' +
        '<div class="mr-modal">' +
          '<button class="mr-close" aria-label="Close">&times;</button>' +
          '<h2 class="mr-title">SIZE GUIDE</h2>' +
          tabs + chartPanel + recPanel +
        '</div>' +
      '</div>';

    var wrap = document.createElement('div');
    wrap.className = 'mr-root';
    wrap.innerHTML = html;
    document.body.appendChild(wrap);
    return wrap;
  }

  function renderResult(chart, resultEl) {
    var H = parseFloat(document.getElementById('mr-h').value);
    var W = parseFloat(document.getElementById('mr-w').value);
    if (!H || !W || H < 120 || H > 220 || W < 35 || W > 180) {
      resultEl.hidden = false;
      resultEl.innerHTML = '<p class="mr-err">Enter a valid height (120-220 cm) and weight (35-180 kg).</p>';
      return;
    }
    var r = recommend(chart, H, W);
    var recSize = chart.sizes[r.rec];
    trackReco(chart, r, recSize, H, W);
    var html = '<div class="mr-rec"><span class="mr-rec-label">We recommend</span>' +
               '<span class="mr-rec-size">' + recSize + '</span>' +
               '<span class="mr-rec-tag">Recommended</span></div>';
    if (r.neighbor > -1) {
      html += '<p class="mr-alt"><b>' + chart.sizes[r.neighbor] + '</b> ' + r.neighborNote + '</p>';
    } else {
      html += '<p class="mr-alt mr-alt--sure">This is a confident fit for your measurements.</p>';
    }
    if (r.adjusted && r.dir !== 0) {
      html += '<p class="mr-note">Adjusted for this style (' + (r.dir > 0 ? 'runs small' : 'runs large') + ').</p>';
    }
    resultEl.hidden = false;
    resultEl.innerHTML = html;
  }

  // ---- Measurement loop: log recommendations + add-to-cart outcomes (mavin-size-recos worker) ----
  // Turns the recommender from an unvalidated guess into something MEASURED: we log what we recommend
  // and what the shopper actually adds to cart. chosen != rec = an override = the model was likely off.
  // PII-free (height/weight/size only), non-blocking (sendBeacon), and skipped inside the theme editor.
  var LOG_BASE = 'https://mavin-size-recos.mavinwear.workers.dev';
  var LAST_REC = null, logTimer = null, addBound = false;
  function inEditor() { return !!(window.Shopify && window.Shopify.designMode); }
  function anonId() {
    try {
      var k = 'mavin_rec_anon', v = localStorage.getItem(k);
      if (!v) { v = 'a' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8); localStorage.setItem(k, v); }
      return v;
    } catch (e) { return null; }
  }
  function pdpHandle() {
    var m = /\/products\/([^\/?#]+)/.exec(location.pathname);
    return m ? m[1] : ((window.MavinFit && window.MavinFit.handle) || null);
  }
  function sendJSON(path, body) {
    try {
      var s = JSON.stringify(body);
      // text/plain keeps this a CORS-simple request -> no preflight (sendBeacon otherwise fails
      // preflight against a credentialed endpoint). The worker parses the text body as JSON.
      if (navigator.sendBeacon) { navigator.sendBeacon(LOG_BASE + path, new Blob([s], { type: 'text/plain;charset=UTF-8' })); return; }
      fetch(LOG_BASE + path, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: s, keepalive: true, mode: 'cors', credentials: 'omit' }).catch(function () {});
    } catch (e) {}
  }
  // Record the recommendation shown. LAST_REC updates synchronously (so a fast add-to-cart compares
  // against the latest); the /log POST is debounced so typing height/weight logs once, when settled.
  function trackReco(chart, r, recSize, H, W) {
    if (inEditor()) return;
    var mf = window.MavinFit || {};
    LAST_REC = { handle: pdpHandle(), rec: recSize, pref: CURRENT_PREF, h: H, w: W };
    var body = {
      handle: LAST_REC.handle, ptype: chart.name || null, fitclass: CURRENT_FIT_CLASS,
      h: H, w: W, pref: CURRENT_PREF, rec: recSize,
      neighbor: (r.neighbor > -1 ? chart.sizes[r.neighbor] : null),
      dir: mf.dir || null, lb: (typeof mf.lb === 'number' ? mf.lb : null), anon: anonId()
    };
    clearTimeout(logTimer);
    logTimer = setTimeout(function () { sendJSON('/log', body); }, 1400);
  }
  // Best-effort read of the size currently selected in the product form (theme-agnostic).
  function selectedSize() {
    var S = ['XS', 'S', 'M', 'L', 'XL', 'XXL'], i, t;
    var picks = document.querySelectorAll('input[type="radio"]:checked,[aria-checked="true"],.is-selected,[data-selected="true"],.selected');
    for (i = 0; i < picks.length; i++) {
      t = (picks[i].value || (picks[i].getAttribute && picks[i].getAttribute('data-value')) || picks[i].textContent || '').trim().toUpperCase();
      if (S.indexOf(t) >= 0) return t;
    }
    var sels = document.querySelectorAll('select');
    for (i = 0; i < sels.length; i++) { t = (sels[i].value || '').trim().toUpperCase(); if (S.indexOf(t) >= 0) return t; }
    return null;
  }
  // When the shopper adds to cart after seeing a recommendation, log the size they actually chose.
  // Read + beacon only -- never blocks or alters the add.
  function bindAddCapture() {
    if (addBound || inEditor()) return; addBound = true;
    document.addEventListener('click', function (e) {
      try {
        var btn = e.target.closest && e.target.closest('[name="add"],button[name="add"],.product-form__submit,[data-add-to-cart],.add-to-cart,.product-form__cart-submit');
        if (!btn || !LAST_REC || !LAST_REC.handle || LAST_REC.handle !== pdpHandle()) return;
        sendJSON('/add', { handle: LAST_REC.handle, rec: LAST_REC.rec, chosen: selectedSize(),
                           pref: LAST_REC.pref, h: LAST_REC.h, w: LAST_REC.w, anon: anonId() });
      } catch (err) {}
    }, true);
  }

  // ---- Wire one product ---------------------------------------------------
  function init() {
    var mount = document.getElementById('mavin-recommender');
    if (!mount || mount.dataset.mrReady) return;
    // Prefer the merchant-editable metaobject data (Content > Metaobjects);
    // fall back to the built-in CHARTS copy if it is missing/unparseable.
    var key = mount.dataset.chart, fbKey = mount.dataset.fallback, DATA = {};
    var dataEl = document.getElementById('mavin-recommender-data');
    if (dataEl) { try { DATA = JSON.parse(dataEl.textContent); } catch (e) { DATA = {}; } }
    // Prefer the product's own chart; otherwise the per-type fallback (rec only).
    var chart = (key && (DATA[key] || CHARTS[key])) || (fbKey && (FALLBACK[fbKey] || CHARTS[fbKey]));
    if (!chart) return;
    mount.dataset.mrReady = '1';

    // Fit class of this garment -> drives the cold-start direction prior when a style
    // has no Estebdal verdict yet (fitted cuts run small, oversized runs large).
    CURRENT_FIT_CLASS = chart.fit || 'regular';
    bindAddCapture();   // measurement loop: capture add-to-cart size vs what we recommended

    // Manual per-product fit override (mavin.size_fit -> data-fit). The live Estebdal verdict
    // (window.MavinFit) takes priority and is read fresh inside recommend() at result time, in
    // case mavin-fit finished resolving after this init ran.
    BASE_FIT = mount.dataset.fit || 'normal';

    // Links row - respect the theme-editor on/off toggles. A fallback (no real
    // chart) forces the Size chart link off and shows recommendation only.
    var showChart = mount.dataset.showChart !== '0';
    var showRec = mount.dataset.showRec !== '0';
    if (!showChart && !showRec) return;
    var parts = [];
    if (showChart) parts.push('<button type="button" class="mr-link" data-open="chart">' + RULER_SVG + ' Size chart</button>');
    if (showRec) parts.push('<button type="button" class="mr-link" data-open="rec">' + CHECK_SVG + ' Size recommendation</button>');
    var row = document.createElement('div');
    row.className = 'mr-links';
    row.innerHTML = parts.join('<span class="mr-dot">&middot;</span>');

    placeLinks(row);

    var modal = null;
    function open(tab) {
      if (!showChart) tab = 'rec';
      else if (!showRec) tab = 'chart';
      if (!modal) { modal = buildModal(chart, tab, showChart, showRec); bindModal(modal, chart); }
      switchTab(modal, tab);
      modal.querySelector('.mr-overlay').classList.add('mr-open');
      document.documentElement.style.overflow = 'hidden';
    }
    row.querySelectorAll('.mr-link').forEach(function (b) {
      b.addEventListener('click', function () { open(b.dataset.open); });
    });
  }

  function bindModal(modal, chart) {
    var overlay = modal.querySelector('.mr-overlay');
    function close() { overlay.classList.remove('mr-open'); document.documentElement.style.overflow = ''; }
    modal.querySelector('.mr-close').addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    modal.querySelectorAll('.mr-tab').forEach(function (t) {
      t.addEventListener('click', function () { switchTab(modal, t.dataset.tab); });
    });
    var resultEl = modal.querySelector('.mr-result');
    var goBtn = modal.querySelector('.mr-go');
    if (goBtn) {
      goBtn.addEventListener('click', function () { renderResult(chart, resultEl); });
      var live = function () {
        var H = parseFloat(document.getElementById('mr-h').value), W = parseFloat(document.getElementById('mr-w').value);
        if (H >= 120 && W >= 35) renderResult(chart, resultEl);
      };
      modal.querySelector('#mr-h').addEventListener('input', live);
      modal.querySelector('#mr-w').addEventListener('input', live);
    }
    // Fit-preference buttons: set the preference and recompute live (if H/W already entered).
    modal.querySelectorAll('.mr-pref-btn').forEach(function (b) {
      b.addEventListener('click', function () {
        CURRENT_PREF = b.dataset.pref || 'true';
        modal.querySelectorAll('.mr-pref-btn').forEach(function (x) { x.classList.toggle('mr-pref-btn--on', x === b); });
        var H = parseFloat(document.getElementById('mr-h').value), W = parseFloat(document.getElementById('mr-w').value);
        if (H >= 120 && W >= 35) renderResult(chart, resultEl);
      });
    });
  }

  function switchTab(modal, tab) {
    modal.querySelectorAll('.mr-tab').forEach(function (t) { t.classList.toggle('mr-tab--on', t.dataset.tab === tab); });
    modal.querySelectorAll('.mr-panel').forEach(function (p) { p.classList.toggle('mr-panel--on', p.dataset.panel === tab); });
  }

  // Place the links where the theme's old "Size guide" link sits and KEEP them
  // there. The theme re-renders the variant picker each time the shopper changes
  // size, which drops our row and un-hides the native link, so we re-apply on
  // every DOM change (debounced to one run per animation frame).
  function placeLinks(row) {
    function anchor() {
      return document.querySelector('.product__size-guide') ||
             document.querySelector('.product__variant-picker') ||
             (function () {
               var btn = document.querySelector('product-form button[name="add"], button[name="add"]');
               return btn && (btn.closest('product-form') || btn.closest('form'));
             })();
    }
    function ensure() {
      // Hide every native "Size guide" link (a fresh one appears on re-render).
      var guides = document.querySelectorAll('.product__size-guide');
      for (var i = 0; i < guides.length; i++) {
        if (guides[i].style.display !== 'none') guides[i].style.display = 'none';
      }
      // Re-insert our row whenever it has been detached by a re-render.
      if (!row.isConnected) {
        var a = anchor();
        if (a && a.parentNode) a.parentNode.insertBefore(row, a);
      }
    }
    ensure();
    var scheduled = false;
    var obs = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; ensure(); });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
